import { EventqSortState, EventOutcomePriority } from '@ecam/eventq-components';

import type { EventAiTagVariant } from './entities/event-detail.model';

export type EventsActionPopoverType = 'manual-patrol' | 'site-check' | null;
export type EventsActionType = 'manual-patrol' | 'site-check';
export type SitesStatusListPopoverType = 'armed' | 'disarmed' | null;
export type SiteArmDisarmPopoverType = 'disarm' | null;
export type CameraArmDisarmPopoverType = 'disarm' | null;
export type SiteDisarmForType = 'until-next-schedule' | 'fixed-time' | 'date-time';
export type SiteDisarmFixedDurationMinutes = number;
export type PendingActionType =
  | EventsActionType
  | 'process-event'
  | 'raise-to-resolve'
  | 'park-event'
  | 'close-event';
export type EventViewPopoverType = 'raise-to-resolve' | 'park-event' | 'close-event' | null;
export type EventViewTab = 'alarms' | 'live-cameras' | 'playback';
export type EventSidePanel = 'cameras' | 'talkdowns';
export type AlarmsFooterTab = 'alarms' | 'arm-disarm';
export type PlaybackViewMode = 'clip' | 'playback';
/** Shared Live / Playback multi-camera grid layout. */
export type CameraGridLayout = '1x1' | '2x2' | '3x3' | '4x3';
/** Shared event-processing panel mode for queue events, site check, and patrol. */
export type EventProcessingMode = 'process-event' | 'site-check' | 'manual-patrol';

export interface AlarmFilters {
  motion: boolean;
  human: boolean;
  vehicle: boolean;
  gun: boolean;
}

export interface PendingAction {
  type: PendingActionType;
  siteId?: string;
  eventId?: string;
  outcomeId?: string;
  outcomeLabel?: string;
  eventLocation?: string;
  closingDetails?: string;
  /** Park duration in minutes (0 = Immediate). */
  durationMinutes?: number;
}

/**
 * Per-browser-tab UI state for the events dashboard and future tabbed views.
 *
 * RULE: Any user-driven state that must differ between tabs (toggles, selection,
 * sort, scroll, panel visibility, wizard step, etc.) MUST be a field here —
 * never a component-local `signal()` on a shared route.
 *
 * When adding a field:
 * 1. Extend this interface
 * 2. Set a default in `createDefaultBrowserTabPageState()`
 * 3. Expose via `computed(() => activePageState().field)` in the page
 * 4. Write via `BrowserTabService.updateActivePageState({ field: value })`
 * 5. Add a `BrowserTabService` spec proving isolation across tab switches
 */
export interface BrowserTabPageState {
  /** In-tab route path after `/{tabKey}/` (default `events` = dashboard). */
  routePath: string;
  showEventQueue: boolean;
  selectedEventId: string | null;
  activeEventId: string | null;
  /** Numeric Dealer site id for the open queue event (live devices until GET /events/{id}). */
  activeEventSiteId: number | null;
  /** Server-issued exclusive handle for `activeEventId` (null when tab has no held event). */
  eventHandleId: string | null;
  /** Event.claimedDateTime for the current pick cycle; processing timer start. */
  claimedAt: string | null;
  eventsSort: EventqSortState | null;
  patrolSort: EventqSortState | null;
  inProcessSort: EventqSortState | null;
  parkedSort: EventqSortState | null;
  expanded: boolean;
  eventsActionPopover: EventsActionPopoverType;
  eventsActionSelectedSiteId: string | null;
  eventsActionSearchQuery: string;
  /** Numeric site ids omitted from Manual Patrol / Site Check; null while occupancy is loading. */
  eventsActionOccupancySiteIds: number[] | null;
  /** Armed/Disarmed sites list popover on the dashboard. */
  sitesStatusListPopover: SitesStatusListPopoverType;
  sitesStatusListSearchQuery: string;
  /** Nested arm/disarm confirm after selecting a site from the status list. */
  siteArmDisarmPopover: SiteArmDisarmPopoverType;
  siteArmDisarmSiteId: string | null;
  siteDisarmReasonId: string | null;
  siteDisarmFor: SiteDisarmForType;
  /** Selected fixed-time duration while Disarm For = Fixed Time Period. */
  siteDisarmFixedDurationMinutes: SiteDisarmFixedDurationMinutes | null;
  /** True while the Custom duration chip is selected. */
  siteDisarmFixedDurationCustom: boolean;
  /** Draft text in the Custom minutes field. */
  siteDisarmFixedDurationCustomDraft: string;
  /** ISO until timestamp while Disarm For = Date & Time. */
  siteDisarmUntilAt: string | null;
  pendingAction: PendingAction | null;
  /** How the event-processing panel behaves for this tab workspace. */
  eventProcessingMode: EventProcessingMode;
  /** ISO end-anchor for the shared "all cameras" 15s playback window. */
  camerasPlaybackAnchorAt: string | null;
  eventViewTab: EventViewTab;
  sidePanel: EventSidePanel;
  cameraSearchQuery: string;
  /** Live / Playback multi-camera list panel open state. */
  cameraListOpen: boolean;
  /** Live / Playback grid layout (cells per page = layout size squared). */
  cameraGridLayout: CameraGridLayout;
  /** 1-based page index into selectedCameraIds for the current layout. */
  cameraGridPage: number;
  /** Cameras open in the Live / Playback grid (shared across those tabs). */
  selectedCameraIds: string[];
  /** Search query for the Live / Playback camera list panel. */
  cameraGridSearchQuery: string;
  talkdownSearchQuery: string;
  /** Talkdown group id while the Activate Talkdown popover is open. */
  activateTalkdownGroupId: string | null;
  alarmFilters: AlarmFilters;
  alarmSort: EventqSortState | null;
  /** Pixel scroll offset of the alarms table body. */
  alarmsTableScrollTop: number;
  alarmFlagOverrides: Record<string, boolean>;
  alarmsFooterTab: AlarmsFooterTab;
  /** Sort for Arm/Disarm cameras table (Cameras / Disarmed At). */
  armDisarmSort: EventqSortState | null;
  /** Client filter for the Arm/Disarm cameras table. */
  armDisarmSearchQuery: string;
  /** Nested camera arm/disarm confirm from Arm/Disarm status icon. */
  cameraArmDisarmPopover: CameraArmDisarmPopoverType;
  cameraArmDisarmCameraId: string | null;
  eventViewPopover: EventViewPopoverType;
  eventViewPopoverSearchQuery: string;
  eventViewPopoverPriorities: EventOutcomePriority[];
  eventViewPopoverSelectedOutcomeId: string | null;
  eventViewPopoverClosingDetails: string;
  /** Selected park duration while the park-event popover is open (0 = Immediate). */
  eventViewPopoverParkDurationMinutes: number;
  selectedCameraId: string | null;
  /**
   * Event id the live tile was auto-seeded (or manually picked) for.
   * A different event on the same tab re-seeds from that event's earliest alarm.
   */
  liveCameraSeededEventId: string | null;
  selectedAlarmId: string | null;
  /** Live camera stream quality; defaults to sub, may be promoted to main. */
  liveStreamType: 'main' | 'sub';
  playbackStreamType: 'main' | 'sub';
  /** Per-camera WebRTC initialize start time (unix ms) on the Playback grid. */
  playbackStreamStartMsByCameraId: Record<string, number>;
  /** Minimal tile: 2s clip vs extended playback window around alarm time. */
  playbackViewMode: PlaybackViewMode;
  /** Optional custom playback window (seconds) when `playbackViewMode` is `playback`. */
  playbackCustomRangeSeconds: number | null;
  /** ISO datetime anchor for playback-mode window center (datetime picker). */
  playbackAnchorTimestamp: string | null;
  /** Centered View Alarm modal open state. */
  viewAlarmOpen: boolean;
  /** Alarm shown in the View Alarm popover (isolated from inline playback selection). */
  viewAlarmSelectedAlarmId: string | null;
  /** Clip vs extended playback inside the View Alarm popover only. */
  viewAlarmPlaybackViewMode: PlaybackViewMode;
  /** ISO datetime anchor for popover playback-mode window (datetime picker). */
  viewAlarmPlaybackAnchorTimestamp: string | null;
  /** AI feedback popover inside the View Alarm modal only. */
  viewAlarmAiFeedbackOpen: boolean;
  viewAlarmAiFeedbackDetectionId: string | null;
  viewAlarmAiFeedbackVariant: EventAiTagVariant | null;
  /** AI feedback popover open for inline playback detection boxes. */
  aiFeedbackOpen: boolean;
  /** Camera id that owns the open inline AI feedback popover (grid tiles share tab state). */
  aiFeedbackCameraId: string | null;
  aiFeedbackDetectionId: string | null;
  aiFeedbackVariant: EventAiTagVariant | null;
  /** Event playback host browser fullscreen for this tab workspace. */
  playbackFullscreen: boolean;
  /** Camera shown in the centered Live Camera popout; null when closed. */
  liveCameraPopoutCameraId: string | null;
  /** PTZ pad open state on the playback/live host. */
  playbackPtzControlsOpen: boolean;
  /** Camera id that owns the open PTZ pad (grid tiles share tab state). */
  playbackPtzControlsCameraId: string | null;
  /** Flag/report popover open on the playback host. */
  playbackFlagReportOpen: boolean;
  /** Camera id that owns the open flag/report popover (grid tiles share tab state). */
  playbackFlagReportCameraId: string | null;
  /** Selected camera report reason while the flag/report popover is open. */
  playbackSelectedReportReasonId: string | null;
}

export const createDefaultAlarmFilters = (): AlarmFilters => ({
  motion: true,
  human: true,
  vehicle: true,
  gun: true,
});

/** Alarms clip tile defaults used when the tab moves to another event. */
export const createAlarmsClipModeReset = (): Pick<
  BrowserTabPageState,
  | 'playbackViewMode'
  | 'playbackCustomRangeSeconds'
  | 'playbackAnchorTimestamp'
  | 'viewAlarmOpen'
  | 'viewAlarmPlaybackViewMode'
  | 'viewAlarmPlaybackAnchorTimestamp'
> => ({
  playbackViewMode: 'clip',
  playbackCustomRangeSeconds: null,
  playbackAnchorTimestamp: null,
  viewAlarmOpen: false,
  viewAlarmPlaybackViewMode: 'clip',
  viewAlarmPlaybackAnchorTimestamp: null,
});

export const createDefaultBrowserTabPageState = (): BrowserTabPageState => ({
  routePath: 'events',
  showEventQueue: true,
  selectedEventId: null,
  activeEventId: null,
  activeEventSiteId: null,
  eventHandleId: null,
  claimedAt: null,
  eventsSort: null,
  patrolSort: null,
  inProcessSort: null,
  parkedSort: null,
  expanded: false,
  eventsActionPopover: null,
  eventsActionSelectedSiteId: null,
  eventsActionSearchQuery: '',
  eventsActionOccupancySiteIds: null,
  sitesStatusListPopover: null,
  sitesStatusListSearchQuery: '',
  siteArmDisarmPopover: null,
  siteArmDisarmSiteId: null,
  siteDisarmReasonId: null,
  siteDisarmFor: 'until-next-schedule',
  siteDisarmFixedDurationMinutes: null,
  siteDisarmFixedDurationCustom: false,
  siteDisarmFixedDurationCustomDraft: '',
  siteDisarmUntilAt: null,
  pendingAction: null,
  eventProcessingMode: 'process-event',
  camerasPlaybackAnchorAt: null,
  eventViewTab: 'alarms',
  sidePanel: 'cameras',
  cameraSearchQuery: '',
  cameraListOpen: false,
  cameraGridLayout: '2x2',
  cameraGridPage: 1,
  selectedCameraIds: [],
  cameraGridSearchQuery: '',
  talkdownSearchQuery: '',
  activateTalkdownGroupId: null,
  alarmFilters: createDefaultAlarmFilters(),
  alarmSort: { column: 'timestamp', direction: 'asc' },
  alarmsTableScrollTop: 0,
  alarmFlagOverrides: {},
  alarmsFooterTab: 'alarms',
  armDisarmSort: { column: 'name', direction: 'asc' },
  armDisarmSearchQuery: '',
  cameraArmDisarmPopover: null,
  cameraArmDisarmCameraId: null,
  eventViewPopover: null,
  eventViewPopoverSearchQuery: '',
  eventViewPopoverPriorities: [],
  eventViewPopoverSelectedOutcomeId: null,
  eventViewPopoverClosingDetails: '',
  eventViewPopoverParkDurationMinutes: 0,
  selectedCameraId: null,
  liveCameraSeededEventId: null,
  selectedAlarmId: null,
  liveStreamType: 'sub',
  playbackStreamType: 'main',
  playbackStreamStartMsByCameraId: {},
  playbackViewMode: 'clip',
  playbackCustomRangeSeconds: null,
  playbackAnchorTimestamp: null,
  viewAlarmOpen: false,
  viewAlarmSelectedAlarmId: null,
  viewAlarmPlaybackViewMode: 'clip',
  viewAlarmPlaybackAnchorTimestamp: null,
  viewAlarmAiFeedbackOpen: false,
  viewAlarmAiFeedbackDetectionId: null,
  viewAlarmAiFeedbackVariant: null,
  aiFeedbackOpen: false,
  aiFeedbackCameraId: null,
  aiFeedbackDetectionId: null,
  aiFeedbackVariant: null,
  playbackFullscreen: false,
  liveCameraPopoutCameraId: null,
  playbackPtzControlsOpen: false,
  playbackPtzControlsCameraId: null,
  playbackFlagReportOpen: false,
  playbackFlagReportCameraId: null,
  playbackSelectedReportReasonId: null,
});

/** Serializable snapshot used when moving a tab to a new browser window. */
export interface BrowserTabPopoutPayload {
  version: 2;
  tab: BrowserTabDisplayMeta;
  pageState: BrowserTabPageState;
  routeUrl: string;
  accessToken: string;
}

export const BROWSER_TAB_POPOUT_LAUNCH_KEY = 'eventq:popout-pending';
export const BROWSER_TAB_POPOUT_STORAGE_PREFIX = 'eventq:tab-popout:';
/** Per-window tab list + page state (sessionStorage — isolated per browser window). */
export const BROWSER_TAB_SESSION_STORAGE_KEY = 'eventq:browser-tab-session';
/** Cross-window registry of open tab workspaces for the signed-in user. */
export const BROWSER_TAB_USER_REGISTRY_KEY = 'eventq:browser-tab-user-registry';
export const BROWSER_TAB_WINDOW_ID_KEY = 'eventq:browser-tab-window-id';
export const BROWSER_TAB_MAX_COUNT = 4;

export interface BrowserTabDisplayMeta {
  id: string;
  label: string;
  /** Stable id for this workspace across popout windows (user-level tab budget). */
  instanceId?: string;
  tags?: string[];
}

/** One open tab workspace counted toward the per-user maximum. */
export interface BrowserTabUserRegistryEntry {
  instanceId: string;
  tabId: string;
  /** Owning window id, or `pending:<popoutId>` while a popout handoff is in flight. */
  windowId: string;
}

export interface BrowserTabUserRegistry {
  version: 1;
  openTabs: BrowserTabUserRegistryEntry[];
}

/** Persisted browser tab workspace for page reload (per browser window). */
export interface BrowserTabSessionSnapshot {
  version: 1;
  tabs: BrowserTabDisplayMeta[];
  activeTabId: string;
  nextTabNumber: number;
  tabPageStates: Record<string, BrowserTabPageState>;
  isPopoutWindow: boolean;
}
